import { useCallback } from 'react';
import { toast } from '@medusajs/ui';
import { useQueryClient } from '@tanstack/react-query';

import { useUpdateCustomer } from '@lib/query/mutations/customer';
import { layoutKeys } from '@lib/query/options/layout';
import {
  useConfirmStorage,
  type ConfirmStorage,
} from './use-confirm-storage';

/** storageKey（customer.metadata key）對應到 LayoutCustomerData 的欄位 */
const layoutCustomerFieldMap = {
  payment_confirm: 'paymentConfirm',
  price_changed_confirm: 'priceChangedConfirm',
} as const satisfies Record<keyof ConfirmStorage, string>;

/**
 * 將確認時間寫入所有含 LayoutCustomerData 的 layout 快取（main / checkout）
 * 不含對應欄位的快取（例如 account layout 的完整 StoreCustomer）維持原樣
 */
const patchLayoutCustomer =
  (field: (typeof layoutCustomerFieldMap)[keyof ConfirmStorage], value: number) =>
  (old: unknown) => {
    if (
      typeof old !== 'object' ||
      old === null ||
      !('customer' in old) ||
      typeof old.customer !== 'object' ||
      old.customer === null ||
      !(field in old.customer)
    ) {
      return old;
    }
    return { ...old, customer: { ...old.customer, [field]: value } };
  };

export interface UseConfirmOptions {
  /** Unix timestamp (milliseconds) of the last time user confirmed */
  lastConfirmedAt?: number;
  /** Unix timestamp (milliseconds) to check against (e.g., first day of month or CMS update time) */
  dateToCheck?: number;
  /** The storage key for tracking 24-hour skip */
  storageKey: keyof ConfirmStorage;
}

/**
 * Generic confirm hook for payment and price-changed confirmations
 *
 * Business Logic:
 * - isConfirmed: true if user has confirmed after dateToCheck
 * - showConfirmModal: true if not confirmed AND not skipped in last 24 hours
 * - confirm(): Updates customer metadata with current timestamp
 * - skip(): Marks as skipped for 24 hours (via localStorage)
 *
 * Design Decision: confirm() 成功後
 * 1. 取消 useUpdateCustomer 觸發的 layout refetch，並直接把確認時間寫入 layout 快取：
 *    - 非作用中的 main layout（staleTime: Infinity）只會被標記過期，回到主要頁面時
 *      會先以舊資料渲染而誤跳 popup
 *    - BFF 客戶快取為各 pod 的記憶體快取，refetch 可能打到尚未清除快取的 pod 而取回舊值
 * 2. 寫入 24h 略過（而非清除）：即使之後短時間內仍取回舊資料，首頁 popup 也不會重跳；
 *    結帳擋單依 isConfirmed 判斷，不受略過影響
 */
export const useConfirm = ({
  lastConfirmedAt,
  dateToCheck,
  storageKey,
}: UseConfirmOptions) => {
  const queryClient = useQueryClient();
  const { waitingConfirmViewedIn24, setWaitingConfirmViewedIn24 } =
    useConfirmStorage();
  const updateCustomerMutation = useUpdateCustomer({
    onError: () => {
      toast.error('確認失敗，請稍後再試');
    },
  });

  // Check if user has confirmed after the dateToCheck
  const isConfirmed =
    !dateToCheck || (lastConfirmedAt ? lastConfirmedAt >= dateToCheck : false);

  // Show modal if not confirmed AND not skipped in the last 24 hours
  const showConfirmModal = !isConfirmed && !waitingConfirmViewedIn24[storageKey];

  // Confirm action - updates customer metadata with current timestamp
  const confirm = useCallback(async () => {
    const confirmedAt = Date.now();
    await updateCustomerMutation.mutateAsync({
      metadata: {
        [storageKey]: confirmedAt,
      },
    });

    await queryClient.cancelQueries({ queryKey: layoutKeys.all });
    queryClient.setQueriesData(
      { queryKey: layoutKeys.all },
      patchLayoutCustomer(layoutCustomerFieldMap[storageKey], confirmedAt)
    );
    setWaitingConfirmViewedIn24((prev) => ({
      ...prev,
      [storageKey]: true,
    }));

    toast.success('已確認，感謝您的閱讀！');
  }, [storageKey, queryClient, setWaitingConfirmViewedIn24, updateCustomerMutation]);

  // Skip action - marks as viewed for 24 hours
  const skip = useCallback(() => {
    setWaitingConfirmViewedIn24((prev) => ({
      ...prev,
      [storageKey]: true,
    }));
  }, [storageKey, setWaitingConfirmViewedIn24]);

  return {
    isConfirmed,
    showConfirmModal,
    confirm,
    skip,
    isLoading: updateCustomerMutation.isPending,
  };
};
